import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Button, Spin, Result, Tag, Space, Modal, Input, message, Typography } from 'antd';
import {
  DownloadOutlined, ArrowLeftOutlined, FileTextOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
} from '@ant-design/icons';

import BillInvoiceHeader from '../components/BillInvoiceHeader';
import BillPartyDetails from '../components/BillPartyDetails';
import GoldItemsViewTable from '../components/GoldItemsViewTable';
import PaymentAndRemarksView from '../components/PaymentAndRemarksView';
import { fetchApprovalInstance, progressApprovalInstance } from '../../approvals/api';
import { fetchBillById, downloadBillPdf, downloadBillInvoicePdf } from '../api';
import './ViewCreatedBill.css';

const { Text } = Typography;

const ViewCreatedBill = ({ bill: billProp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { billId: urlBillId } = useParams();
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();

  // State for bill fetched from DB
  const [fetchedBill, setFetchedBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Prefer prop, then location.state, then fetched from DB
  const bill = billProp || location.state?.bill || fetchedBill;

  // Fetch bill from DB when navigated via URL param and no prop/state available
  useEffect(() => {
    if (billProp || location.state?.bill) return; // already have data
    if (!urlBillId) return; // no ID in URL to fetch

    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setFetchError(null);
        const resp = await fetchBillById(urlBillId);
        if (!mounted) return;
        // resp may be { data: bill } or the bill directly
        setFetchedBill(resp?.data || resp);
      } catch (e) {
        if (!mounted) return;
        console.error('Error loading bill:', e);
        setFetchError('Failed to load bill. Please try again.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [urlBillId, billProp, location.state?.bill]);

  // Support both { data: {...} } and flat {...} bill shapes safely
  const billData = bill?.data || bill || {};
  const hasBillData = !!(bill && (bill.data || bill.customer || bill.billId));

  const {
    customer = {},
    billId,
    goldDetails = [],
    purchaseDate,
    processingFees = 0,
    roundingUpAmount = 0,
    finalAmount,
    images = [],
    branch,
    createdBy,
  } = billData;

  const branchName = branch?.name || billData.branchLocation || null;

  // Approval status handling
  const initialApprovalStatus = bill?.approvalInstance?.status || bill?.data?.approvalInstance?.status || null;
  const [approvalStatus, setApprovalStatus] = useState(initialApprovalStatus);
  const [checkingApproval, setCheckingApproval] = useState(false);
  const [acting, setActing] = useState(false);
  const approvalInstance = bill?.approvalInstance || bill?.data?.approvalInstance || null;
  const openedFromApprovals = !!(bill?.fromApprovals || bill?.data?.fromApprovals);

  useEffect(() => {
    let mounted = true;

    // If status known from creation response, skip fetch
    if (initialApprovalStatus || !billData?._id) return;

    (async () => {
      try {
        setCheckingApproval(true);
        const resp = await fetchApprovalInstance({ module: 'Bill', objectId: billData._id, page: 1, limit: 1 });
        // resp likely { data: [ ...instances ], meta: {...} }
        const arr = Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.data?.data)
          ? resp.data.data
          : [];
        if (!mounted) return;
        setApprovalStatus(arr[0]?.status ?? null);
      } catch (e) {
        if (!mounted) return;
        setApprovalStatus(null);
      } finally {
        if (mounted) setCheckingApproval(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [billData?._id, initialApprovalStatus]);

  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    const billObjectId = billData?._id;
    if (!billObjectId) {
      messageApi.error('Bill reference is missing — reload the page and try again.');
      return;
    }
    try {
      setDownloading(true);
      await downloadBillPdf(billId, billObjectId);
      messageApi.success('Bill PDF downloaded');
    } catch (error) {
      console.error('Download failed:', error);
      messageApi.error(error?.message || 'Failed to download the bill PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Invoice Download — single-page A4 copy of this view, built by the backend
  // invoice service (used for GST filings). Separate from "Download PDF".
  const [exportingInvoice, setExportingInvoice] = useState(false);

  const handleInvoiceDownload = async () => {
    const billObjectId = billData?._id;
    if (!billObjectId) {
      messageApi.error('Bill reference is missing — reload the page and try again.');
      return;
    }
    try {
      setExportingInvoice(true);
      await downloadBillInvoicePdf(billId, billObjectId);
      messageApi.success('Invoice downloaded');
    } catch (error) {
      console.error('Invoice download failed:', error);
      messageApi.error(error?.message || 'Failed to download the invoice');
    } finally {
      setExportingInvoice(false);
    }
  };

  const handleDecision = (action) => {
    if (!approvalInstance?._id) return;
    let comment = '';

    modal.confirm({
      title: action === 'approved' ? 'Approve this bill?' : 'Reject this bill?',
      icon: action === 'approved' ? <CheckCircleOutlined /> : <CloseCircleOutlined />,
      okText: action === 'approved' ? 'Approve' : 'Reject',
      okButtonProps: { danger: action !== 'approved' },
      content: (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">A comment is recorded against this decision.</Text>
          <Input.TextArea
            rows={3}
            style={{ marginTop: 8 }}
            placeholder="Reason / comment"
            onChange={(e) => { comment = e.target.value; }}
          />
        </div>
      ),
      onOk: async () => {
        if (!comment.trim()) {
          messageApi.warning('Please add a comment before submitting.');
          return Promise.reject(new Error('comment required'));
        }
        try {
          setActing(true);
          const resp = await progressApprovalInstance({
            instanceId: approvalInstance._id, action, comment,
          });
          setApprovalStatus(resp?.status || action);
          messageApi.success(`Bill ${action}`);
        } catch (e) {
          messageApi.error('Failed to submit the decision');
        } finally {
          setActing(false);
        }
      },
    });
  };

  // Allow print/download if approved, or if no approval workflow applies
  const isPrintAllowed = approvalStatus === 'completed' || approvalStatus === null;

  const approvalTag = () => {
    if (checkingApproval) return <Tag icon={<ClockCircleOutlined />}>Checking approval…</Tag>;
    if (approvalStatus === 'completed') return <Tag color="green" icon={<CheckCircleOutlined />}>Approved</Tag>;
    if (approvalStatus === 'pending') return <Tag color="orange" icon={<ClockCircleOutlined />}>Pending Approval</Tag>;
    if (approvalStatus === 'rejected') return <Tag color="red" icon={<CloseCircleOutlined />}>Rejected</Tag>;
    return null;
  };

  /* ─── Loading / empty states ─── */
  if (loading) {
    return (
      <div className="bill-page">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: 80 }}>
          <Spin size="large" />
          <Text type="secondary">Loading bill…</Text>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="bill-page">
        <Result
          status="error"
          title="Could not load this bill"
          subTitle={fetchError}
          extra={<Button type="primary" onClick={() => window.location.reload()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!hasBillData) {
    return (
      <div className="bill-page">
        <Result
          status="404"
          title="No bill data found"
          subTitle="This bill may have been removed, or the link is incomplete."
          extra={<Button onClick={() => navigate('/billing')}>Back to Billing</Button>}
        />
      </div>
    );
  }

  return (
    <div className="bill-page">
      {contextHolder}
      {modalContextHolder}

      {/* ── Action bar — screen only ── */}
      <div className="bill-actionbar">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
        {approvalTag()}
        <div className="bill-actionbar__spacer" />

        {openedFromApprovals && approvalInstance && approvalStatus === 'pending' ? (
          <Space>
            <Button danger loading={acting} icon={<CloseCircleOutlined />} onClick={() => handleDecision('rejected')}>
              Reject
            </Button>
            <Button type="primary" loading={acting} icon={<CheckCircleOutlined />} onClick={() => handleDecision('approved')}>
              Approve
            </Button>
          </Space>
        ) : isPrintAllowed ? (
          <Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={handleDownload}
            >
              Download PDF
            </Button>
            <Button
              icon={<FileTextOutlined />}
              loading={exportingInvoice}
              onClick={handleInvoiceDownload}
            >
              Invoice Download
            </Button>
          </Space>
        ) : (
          <Text type="secondary">
            {approvalStatus === 'pending'
              ? 'Printing unlocks once this bill is approved.'
              : 'This bill was rejected and cannot be printed.'}
          </Text>
        )}
      </div>

      {/* ── The document ── */}
      <div className="bill-sheet">
        <BillInvoiceHeader
          billId={billId}
          billDate={purchaseDate}
          branchName={branchName}
        />

        <BillPartyDetails customer={customer} images={images} />

        <GoldItemsViewTable
          goldDetails={goldDetails}
          processingFees={processingFees}
          roundingUpAmount={roundingUpAmount}
          finalAmount={finalAmount}
        />

        <PaymentAndRemarksView form={billData} setForm={() => {}} readOnly />

        {/* ── Signatures ── */}
        <div className="bill-signatures">
          <div className="bill-signature">
            <div className="bill-signature__line" />
            <div className="bill-signature__label">Seller's Signature</div>
          </div>
          <div className="bill-signature">
            <div className="bill-signature__line" />
            <div className="bill-signature__label">For Auriksha Edge Private Limited</div>
          </div>
        </div>

        <div className="bill-footnote">
          This is a computer-generated purchase invoice.
          {createdBy?.name ? ` Prepared by ${createdBy.name}.` : ''}
          {billId ? ` Reference ${billId}.` : ''}
        </div>
      </div>
    </div>
  );
};

export default ViewCreatedBill;
